/**
 * Wreck and salvage part views (M2 plan §6 M2-20): combat, death and contract wrecks as ship-model instances
 * with the damaged ship look (`setDamage(views.wreckDamage)`, no thrust, engine and light sockets dark), and every
 * attached or freed, not yet secured part as a prop instance of its component type, both pooled per key
 * (modelPool.ts). Derelict sections are not drawn here: they are modules of the Harrow, which the field feature
 * already draws; only their parts get views.
 *
 * Poses come from the salvage runtime's SoA pools (float64 world, one tick old); the frame draws them `back`
 * seconds earlier along their velocity (back = (alpha − 1) × SIM_DT ≤ 0), the same interpolated time the ship views
 * use, and turns them into render space (D-011). `frameWrecks` / `frameParts` are allocation-free once warm (D-014).
 */
import type { Object3D } from 'three/webgpu'
import type { LoadedModel } from '../../assets/loader'
import type { Vec3 } from '../../core/math'
import type { WaresData } from '../../data/m2Schemas'
import type { ShipDef } from '../../data/schemas'
import { toRenderSpace } from '../../render/floatingOrigin'
import { applyShipLook, applyStaticLook, type MaterialLibrary } from '../../render/materials'
import { WRECK_KIND, type PartPool, type WreckPool } from '../../space/contracts'
import type { ViewsConfig } from '../spacefx/config'
import { acquireView, beginViews, createModelViewPool, keepView, releaseStale, type ModelViewPool, type PooledModelView } from './modelPool'

/** What the view builders read (deferred models appear in `models` as they load). */
export interface WreckViewDeps {
  readonly lib: MaterialLibrary
  readonly models: ReadonlyMap<string, LoadedModel>
  readonly defs: readonly ShipDef[]
  readonly wares: Pick<WaresData, 'components'>
  readonly views: ViewsConfig
  /** LOD distance scale for new instances. */
  lodScale: number
}

/** Engine plumes and nav lights hang off these sockets; a wreck keeps them dark. */
const DARK_SOCKET_PREFIXES = ['ENG_', 'LIGHT_'] as const

function darkenSockets(sockets: ReadonlyMap<string, Object3D>): void {
  for (const [name, node] of sockets) {
    for (const prefix of DARK_SOCKET_PREFIXES) if (name.startsWith(prefix)) node.visible = false
  }
}

/** A wreck view of ship def `defIndex` (null until its model is loaded). */
export function buildWreckView(deps: WreckViewDeps, defIndex: number): PooledModelView | null {
  const def = deps.defs[defIndex]
  if (!def) return null
  const model = deps.models.get(def.assetId)
  if (!model) return null
  const instance = model.instantiate(deps.lodScale)
  const look = applyShipLook(deps.lib, instance, def.faction)
  look.setThrust(0, 0, false)
  look.setControl(0, 0, 0, 0, 0)
  look.setDamage(deps.views.wreckDamage)
  darkenSockets(instance.sockets)
  instance.root.name = `wreck:${def.id}`
  return { key: defIndex, instance, look, owner: -1, stamp: -1 }
}

/** A part view of component type index `type` (null until its prop is loaded). */
export function buildPartView(deps: WreckViewDeps, type: number): PooledModelView | null {
  const comp = deps.wares.components[type]
  if (!comp) return null
  const model = deps.models.get(comp.propAsset)
  if (!model) return null
  const instance = model.instantiate(deps.lodScale)
  applyStaticLook(deps.lib, instance, deps.views.partLook)
  instance.root.name = `part:${comp.type}`
  return { key: type, instance, look: null, owner: -1, stamp: -1 }
}

/** A pool rotation into `root` (an unset all-zero quaternion reads as identity). */
function setRotation(root: Object3D, x: number, y: number, z: number, w: number): void {
  if (x === 0 && y === 0 && z === 0 && w === 0) root.quaternion.set(0, 0, 0, 1)
  else root.quaternion.set(x, y, z, w)
}

export interface WreckViews {
  readonly wrecks: ModelViewPool
  readonly parts: ModelViewPool
}

export function createWreckViews(parent: Object3D, deps: WreckViewDeps, wreckCapacity: number, partCapacity: number): WreckViews {
  return {
    wrecks: createModelViewPool(parent, wreckCapacity, deps.defs.length, (k) => buildWreckView(deps, k)),
    parts: createModelViewPool(parent, partCapacity, deps.wares.components.length, (k) => buildPartView(deps, k)),
  }
}

/** Wreck slot `i` gets a view: alive and a ship wreck (derelict sections are modules of the Harrow). */
function wreckDrawn(w: WreckPool, i: number): boolean {
  return w.alive[i] === 1 && w.kind[i] !== WRECK_KIND.derelict && w.defIndex[i] >= 0
}

/** Part slot `i` gets a view: alive and not secured. */
function partDrawn(p: PartPool, i: number): boolean {
  return p.alive[i] === 1 && p.secured[i] !== 1
}

/** Draws every live ship wreck (not derelict sections). Allocation-free once warm. */
export function frameWrecks(pool: ModelViewPool, w: WreckPool, back: number, cam: Vec3): void {
  beginViews(pool)
  const n = w.high < w.capacity ? w.high : w.capacity
  for (let i = 0; i < n; i++) if (wreckDrawn(w, i)) keepView(pool, i, w.defIndex[i])
  releaseStale(pool)
  for (let i = 0; i < n; i++) {
    if (!wreckDrawn(w, i)) continue
    const view = acquireView(pool, i, w.defIndex[i])
    if (!view) continue
    const root = view.instance.root
    toRenderSpace(w.x[i] + w.vx[i] * back, w.y[i] + w.vy[i] * back, w.z[i] + w.vz[i] * back, cam, root.position)
    setRotation(root, w.qx[i], w.qy[i], w.qz[i], w.qw[i])
  }
}

/** Draws every live part not yet secured (attached or freed). Allocation-free once warm. */
export function frameParts(pool: ModelViewPool, p: PartPool, back: number, cam: Vec3): void {
  beginViews(pool)
  const n = p.high < p.capacity ? p.high : p.capacity
  for (let i = 0; i < n; i++) if (partDrawn(p, i)) keepView(pool, i, p.type[i])
  releaseStale(pool)
  for (let i = 0; i < n; i++) {
    if (!partDrawn(p, i)) continue
    const view = acquireView(pool, i, p.type[i])
    if (!view) continue
    const root = view.instance.root
    toRenderSpace(p.x[i] + p.vx[i] * back, p.y[i] + p.vy[i] * back, p.z[i] + p.vz[i] * back, cam, root.position)
    setRotation(root, p.qx[i], p.qy[i], p.qz[i], p.qw[i])
  }
}
